<script>
import ElementIcon from "../../../elements/element-icon.vue";
import SquareButton from "../../../common/squareButton.vue";
import CalendarEventMenu from "./calendar-event-menu.vue";
import CalendarEvent from "./CalendarEvent";
import VTooltipWrapper from "../../../../common/v-tooltip-wrapper.vue";
import CalendarPrivileges from "../CalendarPrivileges";
import CalendarThumbnail from "./calendar-thumbnail.vue";
import CalendarEventAllDayIcon from "./calendar-event-all-day-icon.vue";
import CalendarElementState from "./calendar-event-element-state.vue";
import CalendarEventTitle from "./calendar-event-title.vue";
import PublishElementTitle from "../../publish-element-title.vue";
import CalendarEventTypeIcon from "./calendar-event-type-icon.vue";

const THUMBNAIL_WIDTH = 300;
const ESTIMATED_MENU_HEIGHT = 260;

export default {
    name: "calendar-event",
    components: {
        CalendarEventTypeIcon,
        PublishElementTitle,
        CalendarEventAllDayIcon,
        CalendarThumbnail,
        VTooltipWrapper,
        CalendarEventMenu,
        SquareButton,
        ElementIcon,
        CalendarElementState,
        CalendarEventTitle
    },
    props: {
        instance: {},
        activeEventId: String,
        privileges: CalendarPrivileges,
        settings: {showThumbnailInEvent: Boolean, slotDuration: String},
        calendarRef: {}
    },
    data() {
        return {
            showMenu: false,
            hasEnoughSpace: false,
            minHeightForThumbnail: THUMBNAIL_WIDTH * 0.6,
            resizeObserver: null,
            dynamicOffsetX: false,
            dynamicOffsetY: false,
            dynamicNudgeBottom: 0,
            dynamicNudgeTop: 0,
            dynamicNudgeLeft: 0,
            dynamicNudgeRight: 0
        }
    },
    computed: {
        thumbnailWidth() {
            return THUMBNAIL_WIDTH;
        },
        event() {
            return new CalendarEvent(this.instance, this.activeEventId, this.privileges, this.calendarRef);
        },
        offsetTooltipX() {
            return this.event.isInMonthView || this.event.isInWeekView;
        },
        offsetTooltipY() {
            return this.event.isInListView;
        },
        tooltipTransition() {
            if (this.event.isInListView || this.event.isInDayView) return 'slide-y-reverse-transition';
            return 'slide-x-reverse-transition';
        },
        nudgeTooltipBottom() {
            if (this.event.isInDayView) return 80;
            return 0;
        },
        containerClasses() {
            return {
                'calendar-event-container': true,
                'calendar-event-container--active': this.event.active,
                'calendar-event-container--scale': this.event.isInDayView || this.event.isInListView
            }
        }
    },
    mounted() {
        this.checkAvailableSpace();

        this.resizeObserver = new ResizeObserver(this.checkAvailableSpace);
        if (this.$refs.eventContainer) {
            this.resizeObserver.observe(this.$refs.eventContainer);
        }
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    methods: {
        checkAvailableSpace() {
            if (this.$refs.eventContainer) {
                const containerHeight = this.$refs.eventContainer.clientHeight;
                this.hasEnoughSpace = containerHeight >= this.minHeightForThumbnail;
            }
        },
        /** @param item {CalendarActionItem} */
        handleMenuButtonClick(item) {
            this.$emit('clickAction', item.id, this.event.elementData);
        },
        handleEventClick() {
            this.calculateDynamicOffsets();
            this.showMenu = true;
            this.$emit('click', this.event.eventId, this.event.elementData);
        },
        handleMenuClose() {
            this.showMenu = false;
        },
        handleMenuChange({property, value}) {
            const element = {...this.event.elementData};
            element.data[property] = value;
            this.$emit('change', element);
        },
        calculateDynamicOffsets() {
            if (!this.$refs.eventContainer || !this.calendarRef) {
                this.dynamicOffsetX = this.offsetTooltipX;
                this.dynamicOffsetY = this.offsetTooltipY;
                this.dynamicNudgeBottom = this.nudgeTooltipBottom;
                this.dynamicNudgeTop = 0;
                this.dynamicNudgeLeft = 0;
                this.dynamicNudgeRight = 0;
                return;
            }

            const eventRect = this.$refs.eventContainer.getBoundingClientRect();
            const calendarRect = this.calendarRef.$el.getBoundingClientRect();

            this.dynamicOffsetX = this.offsetTooltipX;
            this.dynamicOffsetY = this.offsetTooltipY;
            this.dynamicNudgeBottom = this.nudgeTooltipBottom;
            this.dynamicNudgeTop = 0;
            this.dynamicNudgeLeft = 0;
            this.dynamicNudgeRight = 0;

            const visibleEventTop = Math.max(eventRect.top, calendarRect.top);
            const visibleEventBottom = Math.min(eventRect.bottom, calendarRect.bottom);

            const clippedAtTop = Math.max(0, calendarRect.top - eventRect.top);

            const spaceAbove = visibleEventTop - calendarRect.top;
            const spaceBelow = calendarRect.bottom - visibleEventBottom;
            const spaceLeft = eventRect.left - calendarRect.left;
            const spaceRight = calendarRect.right - eventRect.right;

            if (clippedAtTop > 0) {
                this.dynamicNudgeBottom = this.nudgeTooltipBottom + clippedAtTop;

                if (spaceBelow < ESTIMATED_MENU_HEIGHT && spaceAbove > spaceBelow) {
                    this.dynamicOffsetY = false; // Show above
                    this.dynamicNudgeBottom = 0;
                    this.dynamicNudgeTop = Math.max(0, ESTIMATED_MENU_HEIGHT - spaceAbove);
                }
            } else {
                if (spaceBelow < ESTIMATED_MENU_HEIGHT && spaceAbove > spaceBelow) {
                    this.dynamicOffsetY = false;
                    this.dynamicNudgeBottom = 0;
                    this.dynamicNudgeTop = Math.max(0, ESTIMATED_MENU_HEIGHT - spaceAbove);
                } else if (spaceBelow < ESTIMATED_MENU_HEIGHT) {
                    this.dynamicNudgeBottom = Math.max(this.nudgeTooltipBottom, ESTIMATED_MENU_HEIGHT - spaceBelow);
                }
            }

            if (spaceRight < this.thumbnailWidth && spaceLeft > spaceRight) {
                this.dynamicOffsetX = false;
                this.dynamicNudgeRight = Math.max(0, this.thumbnailWidth - spaceLeft);
            } else if (spaceRight < this.thumbnailWidth) {
                this.dynamicNudgeLeft = Math.max(0, this.thumbnailWidth - spaceRight);
            }

            if (eventRect.bottom > calendarRect.bottom) {
                const clippedAtBottom = eventRect.bottom - calendarRect.bottom;
                this.dynamicNudgeTop = Math.max(this.dynamicNudgeTop, clippedAtBottom);
            }
        }
    },
}
</script>

<template>
    <v-menu
        :value="showMenu"
        :transition="tooltipTransition"
        :max-width="thumbnailWidth"
        :offset-x="dynamicOffsetX"
        :offset-y="dynamicOffsetY"
        :nudge-bottom="dynamicNudgeBottom"
        :nudge-top="dynamicNudgeTop"
        :nudge-left="dynamicNudgeLeft"
        :nudge-right="dynamicNudgeRight"
        :close-on-content-click="false"
    >
        <template v-slot:activator="{ on, attrs }">
            <div
                ref="eventContainer"
                v-bind="attrs"
                v-on="on"
                :class="containerClasses"
                :style="event.containerStyles"
                @click="handleEventClick"
            >
                <div class="calendar-event-icon calendar-event-content">
                    <calendar-element-state :event="event" :settings="settings" />
                </div>

                <calendar-event-title :event="event" :settings="settings" class="calendar-event-title calendar-event-content" />

                <calendar-event-all-day-icon :event="event" class="calendar-event-all-day-icon calendar-event-content" />

                <template v-if="event.getDisplayExtraInformation(settings)">
                    <calendar-event-type-icon :event="event" class="calendar-event-content calendar-event-icon calendar-event-type" />

                    <span class="calendar-event-timestamp calendar-event-content text-truncate">
                        {{ instance.timeText }}
                    </span>

                    <div v-if="hasEnoughSpace && settings.showThumbnailInEvent" class="calendar-event-thumbnail calendar-event-content">
                        <calendar-thumbnail :element="event.elementData" />
                    </div>
                </template>
            </div>
        </template>

        <calendar-event-menu
            :width="thumbnailWidth"
            :event="event"
            @click="handleMenuButtonClick"
            @close="handleMenuClose"
            @change="handleMenuChange"
        />
    </v-menu>
</template>

<style lang="scss">
.calendar-event {
    $base-color: var(--calendar-event-background-color);
    $transparent-base-color: color-mix(in srgb, $base-color 10%, transparent);
    $solid-color: color-mix(in srgb, $base-color 100%, transparent);

    // Main container
    &-container {
        height: 100%;
        width: 100%;
        padding: 4px;
        position: relative;
        overflow: hidden;
        color: black;
        border-left: 8px solid $solid-color;
        border-radius: 4px;
        background-color: $transparent-base-color;

        // Grid layout
        display: grid;
        grid-template-columns: 32px 1fr 32px;
        grid-template-rows: 32px auto 1fr;
        grid-template-areas:
          "icon title allday"
          "type timestamp ..."
          "thumbnail thumbnail thumbnail";

        // Common styles for the overlay
        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background-color: $solid-color;
            z-index: 0;
            pointer-events: none;
        }

        // Default slide transition setup - no transitions initially
        &:not(.calendar-event-container--scale)::before {
            width: 0;
        }

        // Active state for slide transition
        &--active:not(.calendar-event-container--scale) {
            color: var(--calendar-event-color);
            transition: color 0.3s ease-out;

            &::before {
                width: 100%;
                transition: width 0.3s ease-out;
            }
        }

        // Scale transition setup - no transitions initially
        &--scale {
            &::before {
                width: 100%;
                opacity: 0;
            }

            // Only apply transitions when active changes
            &.calendar-event-container--active {
                color: var(--calendar-event-color);
                transition: transform 0.3s ease-out, color 0.3s ease-out;

                &::before {
                    opacity: 1;
                    transition: opacity 0.3s ease-out;
                }
            }
        }

        // Ensure all content is above the background overlay
        .calendar-event-content {
            position: relative;
            z-index: 1;
        }
    }

    // Grid area assignments
    &-icon {
        grid-area: icon;
        display: flex;
    }

    &-title {
        grid-area: title;
    }

    &-all-day-icon {
        grid-area: allday;
    }

    &-type {
        grid-area: type;
    }

    &-timestamp {
        grid-area: timestamp;
        font-weight: bold;
        padding-bottom: 8px;
    }

    &-thumbnail {
        grid-area: thumbnail;
        width: 100%;
        max-width: 200px;
        padding-right: 8px;
    }
}

// Fix icon color
.calendar-event-container {
    &--active {
        .element-state-icon-container {
            color: var(--calendar-event-color) !important;
            transition: color 0.3s ease-out;
        }

        .element-state-icon::before {
            color: var(--calendar-event-color) !important;
            transition: color 0.3s ease-out;
        }
    }
}
</style>
